"use client";

import HappeningList from "../components/event_components/HappeningListing";
import HorizontalScrollList from "../components/event_components/HorizontalScroll";
import MainLayout from "../components/MainLayout";
import Header from "../components/event_components/Header";
import EventForm from "../components/event_components/EventForm";
import EventHandler from "../components/event_components/EventHandler";

import { useEffect, useState, useCallback } from "react";
import {
  fetchCommunityEvents,
  fetchCommercialEvents,
} from "../services/events";
import { successToast,errorToast } from '../utils/toasters'
import { Loader2 } from "lucide-react";

const DemoPage = () => {
  const [showForm, setShowForm] = useState(false);
  const [happeningItems, setHappeningItems] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedCity, setSelectedCity] = useState("Austin");
  const [hasPendingData, setHasPendingData] = useState(false);

  // Pagination states
  const [communityPage, setCommunityPage] = useState(1);
  const [commercialPage, setCommercialPage] = useState(1);
  const [hasMoreCommunity, setHasMoreCommunity] = useState(true);
  const [hasMoreCommercial, setHasMoreCommercial] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    const pendingData = localStorage.getItem("pendingEventData");
    if (pendingData) {
      setHasPendingData(true);
    }
  }, []);

  // Reset pagination when city changes
  useEffect(() => {
    setCommunityPage(1);
    setCommercialPage(1);
    setHasMoreCommunity(true);
    setHasMoreCommercial(true);
    setHappeningItems([]);
  }, [selectedCity]);

  // Initial fetch
  useEffect(() => {
    fetchInitialEvents();
  }, [selectedCity]);

  const fetchInitialEvents = async () => {
    setIsProcessing(true);
    try {
      await fetchEvents(1, 1, true);
    } catch (error) {
      console.error("Error while fetching initial events: ", error);
      errorToast("Error while fetching events");
    } finally {
      setIsProcessing(false);
    }
  };

  const fetchEvents = async (commPage, commrcPage, isInitial = false) => {
    try {
      const promises = [];

      // Fetch commercial events if there are more available
      if (hasMoreCommercial || isInitial) {
        promises.push(
          fetchCommercialEvents({
            city: selectedCity,
            page: commrcPage,
            limit: ITEMS_PER_PAGE,
          })
        );
      } else {
        promises.push(Promise.resolve({ data: [], message: [] }));
      }

      // Fetch community events if there are more available
      if (hasMoreCommunity || isInitial) {
        promises.push(
          fetchCommunityEvents({
            city: selectedCity,
            page: commPage,
            limit: ITEMS_PER_PAGE,
          })
        );
      } else {
        promises.push(Promise.resolve({ data: [] }));
      }

      const [commercialResponse, communityResponse] = await Promise.all(
        promises
      );

      const communityEvents = communityResponse.data || [];
      const commercialEvents =
        commercialResponse.message || commercialResponse.data || [];


      // Check if there are more items available using backend pagination info
      const hasMoreCommData =
        communityResponse.pagination?.hasNextPage ??
        communityEvents.length === ITEMS_PER_PAGE;
      const hasMoreCommercialData =
        commercialResponse.pagination?.hasNextPage ??
        commercialEvents.length === ITEMS_PER_PAGE;

      setHasMoreCommunity(hasMoreCommData);
      setHasMoreCommercial(hasMoreCommercialData);

      // Commercial events take priority, then community events
      const newEvents = [...commercialEvents, ...communityEvents];

      if (isInitial) {
        setHappeningItems(newEvents);
      } else {
        setHappeningItems((prev) => [...prev, ...newEvents]);
      }

      return newEvents.length > 0;
    } catch (error) {
      console.error("Error while fetching events: ", error);
      if (isInitial) {
        errorToast("Error while fetching events");
      }
      return false;
    }
  };

  const loadMoreEvents = useCallback(async () => {
    if (isLoadingMore || (!hasMoreCommunity && !hasMoreCommercial)) {
      return;
    }

    setIsLoadingMore(true);

    try {
      // Determine which pages to fetch next
      let nextCommunityPage = communityPage;
      let nextCommercialPage = commercialPage;

      // Increment pages for APIs that still have data
      if (hasMoreCommunity) {
        nextCommunityPage = communityPage + 1;
        setCommunityPage(nextCommunityPage);
      }

      if (hasMoreCommercial) {
        nextCommercialPage = commercialPage + 1;
        setCommercialPage(nextCommercialPage);
      }

      await fetchEvents(nextCommunityPage, nextCommercialPage, false);
    } catch (error) {
      console.error("Error loading more events:", error);
      errorToast("Error loading more events");
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    communityPage,
    commercialPage,
    hasMoreCommunity,
    hasMoreCommercial,
    selectedCity,
    isLoadingMore,
  ]);


  return (
    <MainLayout rs={false}>
      <EventHandler />

      <Header
        selectedCity={selectedCity}
        setSelectedCity={setSelectedCity}
        onClick={() => setShowForm(true)}
      />
      <div className="container mx-auto py-3">
        {isProcessing && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
            <Loader2 className="animate-spin" width={64} height={64} />
          </div>
        )}

        <HappeningList
          items={happeningItems}
          title="Happening Near You"
          subtitle=""
          onLoadMore={loadMoreEvents}
          isLoadingMore={isLoadingMore}
          hasMore={hasMoreCommunity || hasMoreCommercial}
        />
      </div>

      <HorizontalScrollList />
      {showForm && <EventForm onClose={() => setShowForm(false)} />}

      {/* Hidden EventForm for handling pending data without showing modal */}
      {hasPendingData && !showForm && (
        <div style={{ display: "none" }}>
          <EventHandler />
        </div>
      )}
    </MainLayout>
  );
};

export default DemoPage;
